import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { changePassword, updateMyProfile } from '../../services/auth';

/**
 * Self-service account page. Lives under /account -- reached from the
 * sidebar's own user block, not the admin nav (any authenticated user can
 * edit their own details, not just admins).
 *
 *   Profile  -- name + email (PATCH /api/auth/me)
 *   Privacy  -- password (POST /api/auth/change-password)
 */

type TabKey = 'profile' | 'privacy';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'profile', label: 'Profile' },
  { key: 'privacy', label: 'Privacy' },
];

export default function AccountSettings() {
  const { user, organization, getToken, updateUser } = useAuth();
  const [tab, setTab] = useState<TabKey>('profile');

  if (!user) {return null;}

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Account</h1>
          <p className="page-subtitle">{user.email}</p>
        </div>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile'
        ? <ProfileTab getToken={getToken} updateUser={updateUser} user={user} organization={organization} />
        : <PrivacyTab getToken={getToken} hasPassword={user.hasPassword} />}
    </div>
  );
}

// ============================================================
// PROFILE -- name + email
// ============================================================

type ProfileProps = {
  user: NonNullable<ReturnType<typeof useAuth>['user']>;
  organization: ReturnType<typeof useAuth>['organization'];
  getToken: ReturnType<typeof useAuth>['getToken'];
  updateUser: ReturnType<typeof useAuth>['updateUser'];
};

function ProfileTab({ user, getToken, updateUser }: ProfileProps) {
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [email, setEmail] = useState(user.email);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState(false);

  const dirty =
    firstName !== (user.firstName || '') ||
    lastName !== (user.lastName || '') ||
    email.trim().toLowerCase() !== user.email;

  function clearMessages() {
    setError('');
    setSuccess('');
    setEmailError(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    clearMessages();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email address.');
      setEmailError(true);
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      const emailChanged = email.trim().toLowerCase() !== user.email;
      const result = await updateMyProfile({ firstName, lastName, email }, token);
      updateUser(result.user, result.organization);
      setSuccess(
        emailChanged
          ? 'Profile saved. Use your new email address the next time you sign in.'
          : 'Profile saved.'
      );
    } catch (err: any) {
      const message = err?.response?.data?.error || err.message || 'Failed to save profile';
      setError(message);
      // 409 (already in use) and the format check both point at the email field.
      setEmailError(/email/i.test(message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-card">
      <h2>Profile</h2>
      <p className="settings-help">
        Your name is shown in the sidebar and on anything you create. Your
        email address is what you sign in with.
      </p>

      {/* noValidate: the email field's own validation is handled below so a
          bad address gets the same inline message + red border as every
          other error here, instead of a transient native browser bubble. */}
      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">{success}</p>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="first-name" className="form-label">First name</label>
          <input
            id="first-name"
            type="text"
            className="form-input"
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); clearMessages(); }}
            disabled={saving}
            autoComplete="given-name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="last-name" className="form-label">Last name</label>
          <input
            id="last-name"
            type="text"
            className="form-input"
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); clearMessages(); }}
            disabled={saving}
            autoComplete="family-name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email" className="form-label">Email address</label>
          <input
            id="email"
            type="email"
            className={`form-input${emailError ? ' input-error' : ''}`}
            value={email}
            onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
            disabled={saving}
            autoComplete="email"
          />
        </div>

        <div className="settings-actions">
          <button className="btn btn-primary" type="submit" disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          {dirty && !saving && (
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => {
                setFirstName(user.firstName || '');
                setLastName(user.lastName || '');
                setEmail(user.email);
                clearMessages();
              }}
            >
              Discard
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// ============================================================
// PRIVACY -- password
// ============================================================

type PrivacyProps = {
  getToken: ReturnType<typeof useAuth>['getToken'];
  hasPassword: boolean;
};

function PrivacyTab({ getToken, hasPassword }: PrivacyProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Which field(s) the current error applies to, so only those get the
  // red-border treatment instead of the whole form.
  const [errorFields, setErrorFields] = useState<Set<'current' | 'new' | 'confirm'>>(new Set());

  const dirty = newPassword.length > 0 || confirmPassword.length > 0 || currentPassword.length > 0;

  function clearMessages() {
    setSuccess('');
    setError('');
    setErrorFields(new Set());
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    clearMessages();

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      setErrorFields(new Set(['new'] as const));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation don\'t match.');
      setErrorFields(new Set(['new', 'confirm'] as const));
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      await changePassword(currentPassword, newPassword, token);
      setSuccess(
        hasPassword
          ? 'Password changed. You\'ll need it next time you sign in.'
          : 'Password set. You can now sign in with it directly instead of a magic link.'
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const message = err?.response?.data?.error || err.message || 'Failed to change password';
      setError(message);
      // Backend messages are ours (see /api/auth/change-password) -- match
      // them to point the red border at the field that's actually wrong.
      setErrorFields(new Set(
        /current password/i.test(message) ? (['current'] as const) : (['new', 'confirm'] as const)
      ));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-card">
      <h2>{hasPassword ? 'Change Password' : 'Set a Password'}</h2>
      <p className="settings-help">
        {hasPassword
          ? 'Choose a new password. This signs you out everywhere else -- you\'ll stay signed in here.'
          : 'Your account was created with a one-time sign-in link and has no password yet. Set one now if you\'d rather log in that way, or keep using "Email Link" on the login page instead.'}
      </p>

      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">{success}</p>}

      {/* noValidate: same reasoning as the Profile tab -- the 8-char check
          is handled below so it gets the same red-border alert box as
          every other error here instead of a native browser bubble. */}
      <form onSubmit={handleSubmit} noValidate>
        {hasPassword && (
          <div className="form-group">
            <label htmlFor="current-password" className="form-label">Current password</label>
            <input
              id="current-password"
              type="password"
              className={`form-input${errorFields.has('current') ? ' input-error' : ''}`}
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); clearMessages(); }}
              disabled={saving}
              autoComplete="current-password"
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="new-password" className="form-label">New password</label>
          <input
            id="new-password"
            type="password"
            className={`form-input${errorFields.has('new') ? ' input-error' : ''}`}
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); clearMessages(); }}
            disabled={saving}
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirm-password" className="form-label">Confirm new password</label>
          <input
            id="confirm-password"
            type="password"
            className={`form-input${errorFields.has('confirm') ? ' input-error' : ''}`}
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); clearMessages(); }}
            disabled={saving}
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <div className="settings-actions">
          <button className="btn btn-primary" type="submit" disabled={!dirty || saving}>
            {saving ? 'Saving…' : (hasPassword ? 'Change Password' : 'Set Password')}
          </button>
        </div>
      </form>
    </div>
  );
}
